/**
 * Historical Pricing Tab - Prices based on historical averages
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { HelpAlert } from '../../components/ui/HelpGuide';
import { Modal } from '../../components/ui/modal';
import { 
  History, 
  CheckCircle,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

export default function HistoricalPricing({ categories, formatCurrency }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  
  const [categoryId, setCategoryId] = useState('all');
  const [calculations, setCalculations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleCalculate = async () => {
    try {
      setLoading(true);
      
      const payload = {};
      if (categoryId && categoryId !== 'all') {
        payload.category_id = categoryId;
      }
      
      const response = await api.post('/pricing/calculate/historical', payload);
      
      if (response.data?.success) {
        const calcs = response.data.data.calculations || [];
        setCalculations(calcs);
        setSummary(response.data.data.summary || null);
        setSelectedProducts(calcs.map(c => c.product_id));
        
        // Set default option based on recommendation
        const defaults = {};
        calcs.forEach(c => {
          defaults[c.product_id] = c.recommended;
        });
        setSelectedOptions(defaults);
        
        toast.success(`${calcs.length} produtos analisados`);
      }
    } catch (err) {
      toast.error('Erro ao analisar histórico');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleOptionChange = (productId, option) => {
    setSelectedOptions(prev => ({ ...prev, [productId]: option }));
  };

  const handleApplyPrices = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    
    try {
      setApplying(true);
      
      const calcsToApply = calculations
        .filter(c => selectedProducts.includes(c.product_id))
        .map(c => {
          const option = selectedOptions[c.product_id] || c.recommended;
          return {
            product_id: c.product_id,
            new_price: c.suggestions[option]
          };
        });
      
      const response = await api.post('/pricing/apply', {
        calculations: calcsToApply
      });
      
      if (response.data?.success) {
        toast.success(`${response.data.data.updated_count} preços atualizados!`);
        setShowConfirmModal(false);
        setCalculations([]);
      }
    } catch (err) {
      toast.error('Erro ao aplicar preços');
    } finally {
      setApplying(false);
    }
  };

  const getPriceChangeIcon = (current, suggested) => {
    if (suggested > current) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (suggested < current) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div className="space-y-6">
      <HelpAlert variant="info" title="Precificação por Histórico">
        O sistema analisa o histórico de preços de cada produto e sugere três opções:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><strong>Manter Atual:</strong> Não alterar o preço</li>
          <li><strong>Usar Média:</strong> Aplicar a média histórica de preços</li>
          <li><strong>Ajustar ao Custo:</strong> Aplicar margem de 30% sobre o custo atual</li>
        </ul>
      </HelpAlert>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Analisar Histórico</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Categorias</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={handleCalculate} 
              disabled={loading}
              className="mt-6"
            >
              <History className="h-4 w-4 mr-2" />
              {loading ? 'Analisando...' : 'Analisar Histórico'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {calculations.length > 0 && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{summary?.total_products || 0}</div>
                <div className="text-sm text-muted-foreground">Total de Produtos</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{summary?.with_history || 0}</div>
                <div className="text-sm text-muted-foreground">Com Histórico</div>
              </CardContent>
            </Card>
          </div>

          {/* Products Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sugestões por Histórico</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y max-h-96 overflow-y-auto">
                {calculations.map((calc) => {
                  const selectedOption = selectedOptions[calc.product_id] || calc.recommended;
                  const suggestedPrice = calc.suggestions[selectedOption];
                  
                  return (
                    <div key={calc.product_id} className="flex items-center gap-4 py-3">
                      <Checkbox
                        checked={selectedProducts.includes(calc.product_id)}
                        onCheckedChange={() => handleSelectProduct(calc.product_id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{calc.product_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {calc.history_count} registros no histórico
                        </div>
                      </div>
                      <div className="text-center w-24">
                        <div className="text-xs text-muted-foreground">Custo Atual</div>
                        <div className="text-sm">{formatCurrency(calc.current_cost)}</div>
                      </div>
                      <div className="text-center w-24">
                        <div className="text-xs text-muted-foreground">Custo Médio</div>
                        <div className="text-sm">{formatCurrency(calc.average_cost)}</div>
                      </div>
                      <div className="text-center w-24">
                        <div className="text-xs text-muted-foreground">Preço Atual</div>
                        <div className="text-sm font-medium">{formatCurrency(calc.current_price)}</div>
                      </div>
                      
                      {/* Options */}
                      <div className="flex items-center gap-1">
                        {Object.entries(calc.suggestions).map(([key, value]) => (
                          <button
                            key={key}
                            onClick={() => handleOptionChange(calc.product_id, key)}
                            className={`px-2 py-1 rounded text-xs transition-colors ${
                              selectedOption === key
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                            title={
                              key === 'keep_current' ? 'Manter atual' :
                              key === 'use_average' ? 'Usar média' :
                              'Ajustar ao custo'
                            }
                          >
                            {formatCurrency(value)}
                          </button>
                        ))}
                      </div>
                      
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      
                      <div className="flex items-center gap-2 w-28">
                        {getPriceChangeIcon(calc.current_price, suggestedPrice)}
                        <Badge variant="outline" className="font-bold">
                          {formatCurrency(suggestedPrice)}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Apply Button */}
          {selectedProducts.length > 0 && (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <span className="font-medium">{selectedProducts.length}</span> produtos selecionados
              </div>
              <Button 
                onClick={() => setShowConfirmModal(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Aplicar Preços Selecionados
              </Button>
            </div>
          )}
        </>
      )}

      {/* Confirm Modal */}
      <Modal
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title="Confirmar Aplicação"
        confirmText="Confirmar"
        loading={applying}
        onConfirm={handleApplyPrices}
      >
        <HelpAlert variant="info">
          Os preços serão atualizados conforme a opção selecionada para cada produto.
        </HelpAlert>
      </Modal>
    </div>
  );
}
